// frontend/packages/business-console/src/services/contentEditorService.js
//
// Thin client over the existing content-editor CMS endpoints
// (backend/routes/contentEditorRoutes.js, mounted at /api/v2/content/*).
// The AI blog writer reuses this for everything that touches Git: list
// existing posts, read a post (for voice exemplars + edit), write the
// draft to the uat branch, check uat-vs-prod status, and publish
// (uat -> main merge). No Git logic lives in the AI surface.
//
// Business context comes from the x-business-id header (axios interceptor
// in @upryzze/services); callers don't pass businessId.

import { api } from '@upryzze/services';

/**
 * List content files across all editable collections on a branch.
 * Returns `[{ id, name, path, collection, modifiedSincePublish }]`.
 * @param {string} [env='uat']
 */
export async function listContent(env = 'uat') {
  const { data } = await api.get('/v2/content', { params: { env } });
  return Array.isArray(data) ? data : [];
}

/**
 * Blog posts only, newest-name first. `modifiedSincePublish` flags drafts
 * that differ from prod (i.e., unpublished changes).
 * @param {string} [env='uat']
 */
export async function listBlogPosts(env = 'uat') {
  const all = await listContent(env);
  return all
    .filter((f) => f.collection === 'blog')
    .sort((a, b) => String(b.path).localeCompare(String(a.path)));
}

/**
 * Read one content file. Returns `{ name, path, sha, content }`.
 * @param {string} path repo path, e.g. 'src/content/blog/foo.md'
 * @param {string} [env='uat']
 */
export async function readContentFile(path, env = 'uat') {
  const { data } = await api.get('/v2/content/file', { params: { path, env } });
  return data;
}

/**
 * Create or update a blog draft on the uat branch. `sha` is required by
 * GitHub to update an existing file (omit for a new file). Returns
 * `{ ok, commitSha, contentSha }`.
 */
export async function writeBlogDraft({ slug, content, sha }) {
  const path = `src/content/blog/${slug}.md`;
  const { data } = await api.put(
    '/v2/content/file',
    { content, ...(sha ? { sha } : {}) },
    { params: { path, env: 'uat' } },
  );
  return data;
}

/**
 * Create or update ANY content draft on the uat branch by full repo path
 * (e.g. 'src/content/services/drain-cleaning.md'). The blog writer has its
 * own slug-based helper; the CMS copilot (service pages, plan 3.1.4) writes
 * generated docs whose collection + path come from the backend, so it uses
 * this generic form. `sha` is required by GitHub to update an existing file
 * (omit for a new file). Returns `{ ok, commitSha, contentSha }`.
 */
export async function writeContentDraft({ path, content, sha }) {
  const { data } = await api.put(
    '/v2/content/file',
    { content, ...(sha ? { sha } : {}) },
    { params: { path, env: 'uat' } },
  );
  return data;
}

/**
 * uat-vs-prod diff: which files are changed / new / deleted. Used to show
 * "unpublished changes" before the client publishes.
 * @param {string} [env='uat']
 */
export async function getContentStatus(env = 'uat') {
  const { data } = await api.get('/v2/content/status', { params: { env } });
  return data;
}

/**
 * Publish: merge the uat draft branch into prod (uat -> main). The push to
 * prod auto-triggers the site deploy. Returns the merge result.
 */
export async function publishContent() {
  const { data } = await api.post('/v2/content/publish', {});
  return data;
}

/**
 * Split a markdown file into `{ frontmatter (raw lines), body }`. Tolerant
 * of a missing front-matter block.
 */
export function splitFrontmatter(content = '') {
  const m = /^---\n([\s\S]*?)\n---\n?([\s\S]*)$/.exec(content);
  if (!m) return { frontmatterRaw: '', body: content };
  return { frontmatterRaw: m[1], body: m[2] };
}

/** Best-effort scalar read from a raw YAML frontmatter block. */
export function readFrontmatterScalar(frontmatterRaw, key) {
  const re = new RegExp(`^${key}:\\s*(.*)$`, 'm');
  const m = re.exec(frontmatterRaw || '');
  if (!m) return '';
  let v = m[1].trim();
  if (
    (v.startsWith('"') && v.endsWith('"')) ||
    (v.startsWith("'") && v.endsWith("'"))
  ) {
    v = v.slice(1, -1);
  }
  return v;
}

/**
 * Insert or replace a single scalar field inside a markdown file's YAML
 * frontmatter block, returning the new file content. The value is
 * YAML-quoted (JSON double-quote form is valid YAML and escapes safely).
 * Used to inject the cover image `cld:` token, which the AI does not
 * generate, into the post before it is written to the repo.
 */
export function upsertFrontmatterField(markdown, key, value) {
  const line = `${key}: ${JSON.stringify(value)}`;
  const { frontmatterRaw, body } = splitFrontmatter(markdown);
  if (!frontmatterRaw) {
    return `---\n${line}\n---\n\n${markdown}`;
  }
  const re = new RegExp(`^${key}:.*$`, 'm');
  const newFm = re.test(frontmatterRaw)
    ? frontmatterRaw.replace(re, line)
    : `${frontmatterRaw}\n${line}`;
  return `---\n${newFm}\n---\n${body}`;
}

/**
 * Turn a stored post into a voice exemplar `{ title, excerpt }` for the
 * AI prompt: the title from frontmatter plus the opening of the body.
 */
export function toVoiceExemplar(content = '') {
  const { frontmatterRaw, body } = splitFrontmatter(content);
  const title = readFrontmatterScalar(frontmatterRaw, 'title') || 'Untitled';
  const excerpt = body.trim().slice(0, 1200);
  return { title, excerpt };
}

